import { ReactElement } from 'react';
import { Controller } from 'react-hook-form';
import { Autocomplete, TextField } from '@mui/material';
import { Typography, RadioButton, Checkbox } from '@weareopenr/component-library';
import { ScheduleSelector } from 'src/components/PublishDetailsView/PublishDetailsView';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes/usePriceSchemes';
import { IPriceScheme } from 'src/types/price.types';
import colors from 'src/assets/theme/colors';
import { IPublishDestination, IPublishSetupViewProps } from '../types';
import styles from './PublishSetupView.module.scss';

const ChipDeleteIcon = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="6" r="5.5" stroke={colors.blueberry} strokeWidth={0.8} />
    <path d="M4 4L8 8M8 4L4 8" stroke={colors.blueberry} strokeWidth={0.8} strokeLinecap="round" />
  </svg>
);

const PublishSetupView = ({
  control,
  register,
  watch,
  menuName,
  mealtimes,
  filteredMealtimes,
  publicationData,
  showSchedule,
}: IPublishSetupViewProps): ReactElement => {
  const canEnableMealtimes = filteredMealtimes.length > 0;
  const { data: priceSchemes = [], isLoading: isPriceSchemesLoading } = usePriceSchemes();

  return (
    <section className={styles['setup']}>
      <Typography size="xxlarge" className={styles['heading']}>
        Publish <strong>{menuName}</strong>
      </Typography>
      <div className={styles['section']}>
        <Typography weight="bold" size="medium">
          Mealtimes
        </Typography>
        <div className={styles['radios']}>
          <RadioButton register={register} name="mealtimes" value="disabled">
            <span className={styles['radio-label']}>
              <Typography size="small" weight="semi-bold">
                Disable Mealtimes
              </Typography>
              <Typography size="xsmall" className={styles['subtitle']}>
                The menu section order will follow that of the &lsquo;All Sections&rsquo; tab
              </Typography>
            </span>
          </RadioButton>
          <RadioButton register={register} name="mealtimes" value="enabled" disabled={!canEnableMealtimes}>
            <span className={styles['radio-label']}>
              <Typography size="small" weight="semi-bold">
                Enable Mealtimes
              </Typography>
              <Typography size="xsmall" className={styles['subtitle']}>
                {canEnableMealtimes
                  ? 'Each mealtime added to the mealtime program will be published'
                  : mealtimes.length
                    ? 'Menu categories have not been assigned to Mealtimes'
                    : 'The menu does not contain Mealtimes'}
              </Typography>
            </span>
          </RadioButton>
        </div>
      </div>

      <div className={styles['section']}>
        <Typography weight="bold" size="medium">
          Pricing
        </Typography>
        <Controller
          name="price-scheme"
          control={control}
          render={({ field }) => {
            const selectedScheme = priceSchemes.find((scheme) => scheme.id === field.value) ?? null;
            return (
              <Autocomplete<IPriceScheme, false, false, false>
                options={priceSchemes}
                loading={isPriceSchemesLoading}
                value={selectedScheme}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.name}
                onChange={(_, data) => field.onChange(data?.id)}
                renderInput={(params) => <TextField size="xsmall" {...params} label="Price Scheme" />}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                className={styles['autocomplete']}
              />
            );
          }}
        />
      </div>

      <div className={styles['section']}>
        <Typography weight="bold" size="medium">
          Channels
        </Typography>
        <Controller
          name="selectedChannels"
          control={control}
          render={({ field }) => {
            const selectedChannels = publicationData.filter((channel) =>
              (field.value ?? []).includes(channel.channel_id),
            );
            return (
              <Autocomplete<IPublishDestination, true, false, false>
                multiple
                disableCloseOnSelect
                options={publicationData}
                value={selectedChannels}
                isOptionEqualToValue={(option, value) => option.channel_id === value.channel_id}
                getOptionLabel={(option) => option.name}
                onChange={(_, data) => field.onChange(data.map((channel) => channel.channel_id))}
                limitTags={4}
                ChipProps={{ deleteIcon: ChipDeleteIcon }}
                renderInput={(params) => <TextField size="xsmall" {...params} label="Publish Channels" />}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.channel_id}>
                    <Checkbox checked={selected} onChange={() => {}} />
                    {option.name}
                  </li>
                )}
                className={styles['autocomplete']}
              />
            );
          }}
        />
      </div>

      {showSchedule && (
        <div className={styles['section']}>
          <ScheduleSelector
            register={register}
            watch={watch}
            title="Publish time & date"
            titleSize="medium"
            titleWeight="bold"
            dateLabel="Start Date & Time"
            nowLabel="Publish now"
            scheduleLabel="Schedule"
            showScheduleName
            showConfirmationCheckbox
            radioContainerClassName={styles['schedule-radios']}
            titleClassName={styles['schedule-title']}
          />
        </div>
      )}
    </section>
  );
};

export default PublishSetupView;
